import { SignalRService } from '@/api/bff';
import { Process } from '@/hooks/useHandleProcess';
import { useSelector } from '@/hooks/useSelector';
import { toBase64 } from '@/utils/base64';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel, } from '@microsoft/signalr';
import React from 'react';
import { useAsync } from 'react-use';

const baseURL = process.env.REACT_APP_SIGNALR_URL;

const encode = <T,>(b: T): string => {
  return toBase64(JSON.stringify(b));
};

interface ISignalRContext {
  connected: boolean;
  connect: (process: CustomProcess) => Promise<void>;
  disconnect: () => Promise<void>;
  subscribeStore: () => Promise<void>;
  unsubscribeStore: () => Promise<void>;
  retrieveStatus: (process: CustomProcess) => Promise<unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  retrieveRedisStatus: (store: string, process: CustomProcess) => Promise<any>;
  send: <T>(body: T) => Promise<void>;
  error: boolean;
  errorMessage: string;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  connection?: HubConnection;
}

type CustomProcess = Process | 'DEFAULT';

export interface SignalRError {
  visible: boolean;
  message: string;
}
export interface DefaultSignalRData {
  userId: string;
  storeCode: string;
  process: CustomProcess;
}

export interface SubscribeGroupBody extends DefaultSignalRData {
  connectionId: string;
}

export interface MessageBody<T> extends DefaultSignalRData {
  data: T;
}
interface SignalRNegotiateResponse {
  url: string;
  accessToken: string;
}

const SignalRContext = React.createContext<ISignalRContext>({
  connected: false,

  connect: async () => {},
  disconnect: async () => {},

  send: async () => {},
  subscribeStore: async () => {},
  unsubscribeStore: async () => {},
  retrieveStatus: async () => {},
  retrieveRedisStatus: async () => {},

  error: false,
  errorMessage: '',
  setError: () => {},
});

export const SignalRContextProvider: React.FC = ({ children }) => {
  const [connection, setConnection] = React.useState<HubConnection>();
  const [process, setProcess] = React.useState<CustomProcess>();
  const [connectionStatus, setConnectionStatus] =
    React.useState<HubConnectionState>(HubConnectionState.Disconnected);
  const [error, setError] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const userId = useSelector(state => state.user.username || '');
  const storeCode = useSelector(
    state => state.currentStore.store?.storeCode || ''
  );

  useAsync(async () => {
    if (!connection && storeCode && userId) {
      const fetchToken = async (): Promise<SignalRNegotiateResponse> => {
        const info = await fetch(
          `${baseURL}/negotiate?storeCode=${storeCode}&userId=${userId}&hubname=srosignalr`,
          {
            method: 'POST',
            headers: {
              'x-ms-signalr-storeCode': storeCode,
              'x-ms-signalr-userId': userId,
            },
          }
        );
        return await info.json();
      };

      const { url, accessToken: initialToken } = await fetchToken();

      let currentToken = initialToken;

      const hubConnection = new HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: async () => {
            try {
              const payload = JSON.parse(
                atob(currentToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
              );
              if (Date.now() < payload.exp * 1000) {
                console.group(`Signalr - Token`);
                console.log(`Token is valid: proceed`);
                console.groupEnd();
                return currentToken;
              }
            } catch {
              console.warn('Token malformato, procedi col refresh');
            }


            console.group(`Signalr - Token`);
            console.log(`Token is expired: refreshing token...`);
            console.groupEnd();
            const { accessToken: newToken } = await fetchToken();
            currentToken = newToken;
            return currentToken;
          },
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect()
        .build();

      setConnection(hubConnection);
    }
  }, [storeCode, userId, connection]);

  const showError = (message: string): void => {
    setError(true);
    setErrorMessage(message);
  };

  const connected = React.useMemo(
    () => connectionStatus === HubConnectionState.Connected,
    [connectionStatus]
  );

  const subscribeStore = React.useCallback(async () => {
    if (connected) {
      if (connection!.connectionId) {
        const body: SubscribeGroupBody = {
          userId,
          storeCode,
          process: process || 'DEFAULT',
          connectionId: connection!.connectionId,
        };

        try {
          await SignalRService.signalRSubscribeProcess({
            requestBody: {
              storeCode: body.storeCode,
              process: body.process,
              userId: body.userId,
            },
          });
          await fetch(`${baseURL}/subscribe`, {
            method: 'POST',
            body: JSON.stringify({
              encoded: encode(body),
            }),
          });

          console.group(`Signalr - Subscribe`);
          console.log(`User ${userId} subscribed to store: ${storeCode}`);
          console.groupEnd();
        } catch {
          showError('An error occured while subscribing store');
        }
      }
    } else {
      showError('You must be connected before subscribe to the store');
    }
  }, [connected, connection, process, storeCode, userId]);

  const unsubscribeStore = React.useCallback(async () => {
    if (connected) {
      if (connection!.connectionId) {
        const body: SubscribeGroupBody = {
          userId,
          storeCode,
          process: process || 'DEFAULT',
          connectionId: connection!.connectionId,
        };

        try {
          await SignalRService.signalRUnSubscribeProcess({
            requestBody: {
              storeCode: body.storeCode,
              process: body.process,
              userId: body.userId,
            },
          });
        } catch {
          showError('An error occured while removing store data');
        }
        try {
          await fetch(`${baseURL}/unsubscribe`, {
            method: 'POST',
            body: JSON.stringify({
              encoded: encode(body),
            }),
          });
        } catch {
          showError('An error occured while subscribing store');
        }
        console.group(`Signalr - Unubscribe`);
        console.log(`User ${userId} unubscribed from store: ${storeCode}`);
        console.groupEnd();
      }
    } else {
      showError('You must be connected before unsubscribe from the store');
    }
  }, [connected, connection, process, storeCode, userId]);

  const send = React.useCallback(
    async <T,>(body: T) => {
      if (connected) {
        const message: MessageBody<T> = {
          userId,
          storeCode,
          process: process || 'DEFAULT',
          data: body,
        };

        try {
          await SignalRService.signalRUpdateStore({
            requestBody: {
              encoded: encode(message),
            },
          });
          await fetch(`${baseURL}/messages`, {
            method: 'POST',
            body: JSON.stringify({
              encoded: encode({
                ...message,
                data: {
                  ...body,
                  epcs: [],
                  products: '',
                },
              }),
            }),
          });

          console.group(`Signalr - Message`);
          console.log(`Message sent to users subscribed at store ${storeCode}`);
          console.log(`Data sent`, message);
          console.groupEnd();
        } catch {
          showError('An error occured while sending data');
        }
      } else {
        showError('You must be connected before send data');
      }
    },
    [connected, process, storeCode, userId]
  );

  const connect = React.useCallback(
    async (process: CustomProcess) => {
      if (connection) {
        try {
          setProcess(process);
          await connection.start();
          setConnectionStatus(connection.state);

          console.group(`Signalr - Connect`);
          console.log(
            `SignalR connection state: ${connection.state}. Process: ${process}`
          );
          console.groupEnd();
        } catch (e) {
          console.log('Connection failed: ', e);
        }
      }
    },
    [connection]
  );

  const disconnect = React.useCallback(async () => {
    if (connection) {
      try {
        await connection.stop();

        setConnectionStatus(connection.state);
        setConnection(undefined);
        setProcess(undefined);

        console.group(`Signalr - Disconnect`);
        console.log(`SignalR connection state: ${connection.state}.`);
        console.groupEnd();
      } catch (e) {
        console.log('Connection failed: ', e);
      }
    }
  }, [connection]);

  const retrieveStatus = React.useCallback(
    async (process: CustomProcess) => {
      if (connection) {
        try {
          const response = await fetch(
            `${baseURL}/status?store=${storeCode}&process=${process}`,
            {
              method: 'GET',
            }
          );

          return await response.json();
        } catch {
          showError('An error occured while retrieving loader status');
        }
      }
    },
    [connection, storeCode]
  );

  const retrieveRedisStatus = React.useCallback(
    async (store: string, process: CustomProcess) => {
      const data = await SignalRService.sirnalRStatus({
        store,
        process,
      });
      return JSON.parse(Buffer.from(data, 'base64').toString());
    },
    []
  );

  return (
    <SignalRContext.Provider
      value={{
        connection,
        connected,
        connect,
        disconnect,
        send,
        subscribeStore,
        unsubscribeStore,
        retrieveStatus,
        retrieveRedisStatus,
        error,
        errorMessage,
        setError,
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalRContext = (): ISignalRContext =>
  React.useContext(SignalRContext);
